import React, { useCallback } from "react";
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  alpha,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { AnalyticsFiltersState, StationData } from "../types";

interface AnalyticsFiltersProps {
  filters: AnalyticsFiltersState;
  onTimeRangeChange: (value: AnalyticsFiltersState["timeRange"]) => void;
  onGroupByChange: (value: AnalyticsFiltersState["groupBy"]) => void;
  onStationChange: (stationId?: string) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
  onExport: () => void;
  stations: StationData[];
}

export const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = ({
  filters,
  onTimeRangeChange,
  onGroupByChange,
  onStationChange,
  onCustomDateChange,
  onExport,
  stations,
}) => {
  const theme = useTheme();

  const handleTimeRangeChange = (event: SelectChangeEvent) => {
    onTimeRangeChange(event.target.value as AnalyticsFiltersState["timeRange"]);
  };

  const handleGroupByChange = (event: SelectChangeEvent) => {
    onGroupByChange(event.target.value as AnalyticsFiltersState["groupBy"]);
  };

  const handleStationChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    onStationChange(value === "all" ? undefined : value);
  };

  const handleStartDateChange = (value: Dayjs | null) => {
    if (value) {
      onCustomDateChange(
        value.startOf("day").toISOString(),
        filters.customEndDate ?? dayjs().toISOString(),
      );
    }
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    if (value) {
      onCustomDateChange(
        filters.customStartDate ?? dayjs().subtract(30, "day").toISOString(),
        value.endOf("day").toISOString(),
      );
    }
  };

  const handleExportClick = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    onExport();
  }, [onExport]);

  return (
    <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap" useFlexGap>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="time-range-label">Time Range</InputLabel>
        <Select
          labelId="time-range-label"
          label="Time Range"
          value={filters.timeRange}
          onChange={handleTimeRangeChange}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="today">Today</MenuItem>
          <MenuItem value="last7">Last 7 days</MenuItem>
          <MenuItem value="last30">Last 30 days</MenuItem>
          <MenuItem value="custom">Custom...</MenuItem>
        </Select>
      </FormControl>

      {filters.timeRange === "custom" && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start date"
            value={filters.customStartDate ? dayjs(filters.customStartDate) : null}
            onChange={handleStartDateChange}
            maxDate={filters.customEndDate ? dayjs(filters.customEndDate) : dayjs()}
            slotProps={{
              textField: { size: "small", sx: { width: 160 } },
            }}
          />
          <DatePicker
            label="End date"
            value={filters.customEndDate ? dayjs(filters.customEndDate) : null}
            onChange={handleEndDateChange}
            maxDate={dayjs()}
            slotProps={{
              textField: { size: "small", sx: { width: 160 } },
            }}
          />
        </LocalizationProvider>
      )}

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel id="group-by-label">Group By</InputLabel>
        <Select
          labelId="group-by-label"
          label="Group By"
          value={filters.groupBy}
          onChange={handleGroupByChange}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="day">Day</MenuItem>
          <MenuItem value="week">Week</MenuItem>
          <MenuItem value="month">Month</MenuItem>
        </Select>
      </FormControl>

      {stations.length > 1 && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="station-label">Station</InputLabel>
          <Select
            labelId="station-label"
            label="Station"
            value={filters.stationId ?? "all"}
            onChange={handleStationChange}
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="all">All Stations</MenuItem>
            {stations.map((station) => (
              <MenuItem key={station.stationId} value={station.stationId}>
                {station.stationName} ({station.reportCount})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<FileDownloadIcon />}
        onClick={handleExportClick}
        sx={{
          borderRadius: 999,
          textTransform: "none",
          transition: "all 0.15s ease-out",
          "&:hover": {
            transform: "scale(1.02)",
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
          },
          "&:active": {
            transform: "scale(0.96)",
            boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.3)}`,
          },
        }}
      >
        Export CSV
      </Button>
    </Stack>
  );
};
